import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Foods = () => {
  const [searchParams] = useSearchParams();
  const user_id = searchParams.get('user_id');
  const { fetchWithAuth } = useAuth();
  
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [zoomedImage, setZoomedImage] = useState(null);
  const limit = 20;

  useEffect(() => {
    fetchFoods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, user_id]);

  const fetchFoods = async () => {
    setLoading(true);
    try {
      const url = user_id 
        ? `/api/foods?page=${page}&limit=${limit}&user_id=${user_id}`
        : `/api/foods?page=${page}&limit=${limit}`;
      
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error('Failed to fetch food logs');
      const data = await response.json();
      setFoods(data.data);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && foods.length === 0) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '24px' }}>
        Food Logs {user_id && `- User ${user_id}`}
      </h1>

      {user_id && (
        <div style={{ marginBottom: '16px' }}>
          <Link to="/foods" className="link">← View All Food Logs</Link>
        </div>
      )}

      <div className="card">
        <div style={{ marginBottom: '16px', color: '#666' }}>
          {total} total records
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>User</th>
                <th>Image</th>
                <th>Food Name</th>
                <th>Calories</th>
                <th>Protein</th>
                <th>Carbs</th>
                <th>Fats</th>
                <th>Serving</th>
                <th>Favorite</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {foods.map((food) => (
                <tr key={food.id}>
                  <td>{food.id}</td>
                  <td>
                    <Link to={`/users/${food.user_id}`} className="link">
                      {food.user_name || `User ${food.user_id}`}
                    </Link>
                  </td>
                  <td>
                    {food.image_url ? (
                      <img 
                        src={food.image_url} 
                        alt={food.name}
                        onClick={() => setZoomedImage(food.image_url)}
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                        }}
                        onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
                        onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                      />
                    ) : (
                      <span style={{ color: '#999', fontSize: '12px' }}>No image</span>
                    )}
                  </td>
                  <td>
                    <strong>{food.name || 'Unnamed'}</strong>
                  </td>
                  <td>{food.calories ? `${Math.round(food.calories)} kcal` : '0'}</td>
                  <td>{food.protein ? `${Math.round(food.protein)}g` : '0g'}</td>
                  <td>{food.carbs ? `${Math.round(food.carbs)}g` : '0g'}</td>
                  <td>{food.fats ? `${Math.round(food.fats)}g` : '0g'}</td>
                  <td>{food.serving ? `${food.serving} ${food.serving_unit || ''}` : 'N/A'}</td>
                  <td>
                    {food.is_favorite ? (
                      <span style={{ fontSize: '16px' }}>⭐</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{formatDate(food.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Previous
          </button>
          <span className="page-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div
          onClick={() => setZoomedImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'pointer',
            padding: '20px',
          }}
        >
          <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
            <button
              onClick={() => setZoomedImage(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              ×
            </button>
            <img
              src={zoomedImage}
              alt="Food"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Foods;


